<template>
  <main class="contact-page">
    <!-- Per-page SEO: unique title + meta description (Requirement 10.1). -->
    <SeoHead :seo="content.seo" />

    <!-- Page intro -->
    <section class="contact-intro-section" aria-labelledby="contact-heading">
      <v-container>
        <ScrollReveal>
          <h1 id="contact-heading" class="contact-heading">
            {{ content.intro.heading }}
          </h1>
          <p class="contact-intro">{{ content.intro.body }}</p>
        </ScrollReveal>
      </v-container>
    </section>

    <!-- Enquiry form + general contact details (full width, at the top). -->
    <section class="contact-section" aria-labelledby="contact-details-heading">
      <v-container>
        <v-row class="contact-row">
          <!-- Company contact details: phone, email (Requirement 7.2). -->
          <v-col cols="12" md="5">
            <ScrollReveal>
              <div class="contact-details" aria-labelledby="contact-details-heading">
                <h2 id="contact-details-heading" class="contact-details__heading">
                  Get in touch
                </h2>

                <div class="contact-detail">
                  <span class="contact-detail__label">Phone</span>
                  <a
                    :href="`tel:${telHref}`"
                    class="contact-detail__value"
                  >
                    {{ details.phone }}
                  </a>
                </div>

                <div class="contact-detail">
                  <span class="contact-detail__label">Email</span>
                  <a
                    :href="`mailto:${details.email}`"
                    class="contact-detail__value"
                  >
                    {{ details.email }}
                  </a>
                </div>

                <p class="contact-details__note">
                  Prefer to visit? Find your nearest office below.
                </p>
              </div>
            </ScrollReveal>
          </v-col>

          <!-- Contact_Form; submission delegated to ContactApi (Requirements 7.1, 7.3). -->
          <v-col cols="12" md="7">
            <ScrollReveal>
              <ContactForm
              :submit-handler="submitEnquiry"
              :challenge-provider="fetchChallenge"
            />
            </ScrollReveal>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Office locations: Melbourne, Brisbane, Darwin, each with an optional map, full width below the form. -->
    <OfficeLocations
      :offices="content.offices"
      :heading="content.officesHeading"
      :subheading="content.officesSubheading"
    />
  </main>
</template>

<script>
// ContactPage — the Contact Us page (Requirement 7).
//
// Renders the ContactForm alongside ThinkSpatial's phone, email, and office
// location (Requirements 7.1, 7.2). Editorial copy and contact details come
// from the structured content module `client/src/content/contact.js` (carried
// over from the legacy ContactUs.vue) so changes need no redesign
// (Requirements 11.2, 10.1).
//
// Form submission is delegated: a `submitHandler` passed to ContactForm calls
// `ContactApi.submitEnquiry`. The form awaits it to drive its confirmation
// state on success and its error state (with value retention) on failure
// (Requirements 7.3, 7.5).
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset.
import contact from '@/content/contact'
import ContactApi from '@/services/ContactApi'
import SeoHead from '@/components/SeoHead.vue'
import ScrollReveal from '@/components/ScrollReveal.vue'
import ContactForm from '@/components/ContactForm.vue'
import OfficeLocations from '@/components/OfficeLocations.vue'

export default {
  name: 'ContactPage',
  components: {
    SeoHead,
    ScrollReveal,
    ContactForm,
    OfficeLocations,
  },
  data () {
    return {
      // Contact_Page content (intro copy, details, form labels, SEO).
      content: contact,
    }
  },
  computed: {
    // Convenience accessor for the company contact details block.
    details () {
      return this.content.details
    },
    // Strip spaces so the `tel:` link dials correctly.
    telHref () {
      return (this.details.phone || '').replace(/\s+/g, '')
    },
  },
  methods: {
    // Submit handler passed to ContactForm. Delegates to the public contact
    // endpoint via ContactApi; resolves on success (the form shows its
    // confirmation) and rejects on failure (the form shows its error state and
    // retains the visitor's entered values).
    submitEnquiry (enquiry) {
      return ContactApi.submitEnquiry(enquiry)
    },
    // Fetches a self-hosted proof-of-work challenge for the form's bot defence.
    // Returns null when the server has proof-of-work disabled.
    fetchChallenge () {
      return ContactApi.fetchChallenge()
    },
  },
}
</script>

<style lang="scss" scoped>
.contact-intro-section {
  padding: 64px 0 8px;
}

.contact-section {
  padding: 8px 0 64px;
}

.contact-heading {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: #41281b;
  margin-bottom: 16px;
}

.contact-intro {
  font-size: 1.15rem;
  color: #4a4a4a;
  line-height: 1.7;
  margin-bottom: 32px;
  max-width: 640px;
}

.contact-row {
  row-gap: 32px;
}

.contact-details__heading {
  font-size: 1.5rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 24px;
}

.contact-detail {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
}

.contact-detail__label {
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #929292;
  margin-bottom: 4px;
}

.contact-detail__value {
  font-size: 1.05rem;
  color: #4a4a4a;
  text-decoration: none;
}

a.contact-detail__value:hover {
  color: #e7511e;
}

.contact-details__note {
  font-size: 0.95rem;
  color: #929292;
  margin-top: 8px;
}
</style>
